/**
 * @typedef {Object} AnnotationNode
 * @property {string} id
 * @property {string} label - Wave label (1, 2, 3, 4, 5, A, B, C, X, Y, W)
 * @property {number} x_px - X position in pixels on the canvas
 * @property {number} y_px - Y position in pixels on the canvas
 * @property {number} x_norm - Normalized X (0-1 relative to canvas width)
 * @property {number} y_norm - Normalized Y (0-1 relative to canvas height)
 * @property {'motive'|'corrective'} wave_type
 * @property {string} color
 */

/**
 * @typedef {Object} AnnotationPayload
 * @property {AnnotationNode[]} nodes
 * @property {number} canvas_width
 * @property {number} canvas_height
 * @property {string} timestamp
 * @property {string} [analysis_id]
 * @property {string} [project_id]
 */

export const WAVE_LABELS = [
  { label: '1', type: 'motive', color: '#f5b400' },
  { label: '2', type: 'motive', color: '#f5b400' },
  { label: '3', type: 'motive', color: '#f5b400' },
  { label: '4', type: 'motive', color: '#f5b400' },
  { label: '5', type: 'motive', color: '#f5b400' },
  { label: 'A', type: 'corrective', color: '#00f5d4' },
  { label: 'B', type: 'corrective', color: '#00f5d4' },
  { label: 'C', type: 'corrective', color: '#00f5d4' },
  { label: 'X', type: 'corrective', color: '#00f5d4' },
  { label: 'Y', type: 'corrective', color: '#00f5d4' },
  { label: 'W', type: 'corrective', color: '#00f5d4' },
];

export const createNode = (label, x_px, y_px, canvasWidth, canvasHeight) => {
  const waveDef = WAVE_LABELS.find(w => w.label === label) || WAVE_LABELS[0];
  return {
    id: `${label}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    label,
    x_px,
    y_px,
    x_norm: canvasWidth > 0 ? x_px / canvasWidth : 0,
    y_norm: canvasHeight > 0 ? y_px / canvasHeight : 0,
    wave_type: waveDef.type,
    color: waveDef.color,
  };
};

/**
 * @typedef {Object} FractalLevel
 * @property {string} label - e.g. "Root", "Wave 3", "Sub-Wave 3"
 * @property {AnnotationNode[]} nodes
 * @property {{startNode: string, endNode: string}} [parentSegment] - IDs of parent nodes forming this region
 * @property {{x: number, y: number, width: number, height: number}} [canvasRegion] - visible region in parent coords
 */

export const buildPayload = (nodes, canvasWidth, canvasHeight, analysisId, projectId, fractalTree) => ({
  nodes: nodes.map(n => ({
    ...n,
    x_norm: canvasWidth > 0 ? n.x_px / canvasWidth : 0,
    y_norm: canvasHeight > 0 ? n.y_px / canvasHeight : 0,
  })),
  canvas_width: canvasWidth,
  canvas_height: canvasHeight,
  timestamp: new Date().toISOString(),
  analysis_id: analysisId || null,
  project_id: projectId || null,
  fractal_tree: fractalTree || null,
});
